import React, { useEffect, useState } from 'react';
import Apirest from '../../../Content/Apirest';
import { loadAvailableTags } from '../../../Commons/Utils/tags';
import { useContent } from '../../../Content/cms';
import './index.css';

import { useFormFields } from '../../../Commons/Components/FormFields';
import { getEmptyValue, PopUpSuccess } from '../../../Commons/Helpers';

// Import interfaces
import { Participant, Locale } from "../../interfaces";
import { FormSubmitData } from '../../../Commons/Components/FormFields/types';
import { FormSectionScheme, FieldScheme, Option } from '../../../Commons/Components/FormFields/types';

// Import the store
import { ADD_OR_UPDATE_PARTICIPANT } from "../../../store/participants/types";
import { store } from "../../../store";

// Import JSX Components
import { Button, Col, Container, Form, Row, Spinner, Badge, InputGroup, Accordion, Card } from "react-bootstrap";
import { FormField } from "../../../Commons/Components/FormFields/Field";
import { RowSpinner } from "../../../Commons/MultipleComponents";


// Load form schema
import form_schema from './form_schema.json';

const STEP_FIELDS: number[][] = [
    [0, 1, 2, 3, 4, 5],
    [6, 7, 8, 9, 10, 11, 12, 13],
];

const CreatePatient : React.FC = () => {

    let getContent = useContent();
    let today= new Date().toISOString().split("T")[0];

    const [step, setStep] = useState<number>(0);
    const [requiredErrors, setRequiredErrors] = useState<string[]>([]);

    const {
        sections,
        setSections,
        submitHandler,
        onChange,
        state
    } = useFormFields();

    const [assignedTags, setAssignedTags] = useState<string[]>([]);
    const [createTagValue, setCreateTagValue] = useState<string>('');
    const [availableTags, setAvailableTags] = useState<string[]>([]);

    function addTag(tag_name: string) {
        tag_name = tag_name.toLowerCase();
        setAssignedTags(prev => {
            if (prev.find(t => t === tag_name) === undefined) {
                return [...prev, tag_name];
            }
            return prev;
        });
    }

    function createTag(e: any) {
        if (e && e.preventDefault) e.preventDefault();
        let tag_name = (createTagValue || '').toString().trim().toLowerCase();
        if (tag_name !== '') {
            addTag(tag_name);
            setAvailableTags(prev => prev.find(t => t === tag_name) ? prev : [...prev, tag_name]);
            setCreateTagValue('');
        }
    }

    function removeTag(tag_name: string) {
        tag_name = tag_name.toLowerCase();
        setAssignedTags(prev => prev.filter(t => t !== tag_name));
    }

    useEffect(
        () => {
            const new_sections : FormSectionScheme[] = [{
                name: '',
                description: '',
                fields: form_schema as FieldScheme[]
            }];

            for (let section of new_sections) {
                for (let field of section.fields) {
                    if (typeof field.options !== 'undefined') {
                        field.options = field.options.map((option: Option) => ({
                            ...option,
                            label: getContent(option.label)
                        }));
                    }
                }
            }

            for (let section of new_sections) {
                for (let field of section.fields) {
                    if (field.name === "diagnose_date") {
                        field.max = today;
                    } else if (field.name === "birthday"){
                        field.max = today;
                    }
                }
            }

            Apirest.get_locales((response: Locale[]) => {
                for (let section of new_sections) {
                    for (let field of section.fields) {
                        if (field.name === 'locale' && field.options === undefined) {
                            field.options = response.map(locale => ({
                                value: locale.id.toString(),
                                label: locale.code
                            }));
                        }
                    }
                }

                setSections(new_sections);
            });

            loadAvailableTags((tags: string[]) => setAvailableTags(tags));
        },
        [] // eslint-disable-line react-hooks/exhaustive-deps
    );

    function onSubmit(data: FormSubmitData) {
        let promise = new Promise<void>(
            (resolve, reject) => {
                const payload = {
                    ...data,
                    tags: assignedTags
                };

                Apirest.create_participant(
                    payload,
                    (participant: Participant) => {
                        store.dispatch({
                            type: ADD_OR_UPDATE_PARTICIPANT,
                            payload: participant
                        });
                        PopUpSuccess(
                            getContent("home__form__server_response__success").replace('{participant_full_name}', participant.code)
                        );
                        setStep(0);
                        setRequiredErrors([]);
                        resolve();
                    },
                    (jqXHR: JQueryXHR) => {
                        reject(jqXHR.responseJSON);
                    }
                );
            }
        );

        return promise;
    }

    function handleNext(e: React.MouseEvent) {
        e.preventDefault();
        if (step === 0) {
            const missing: string[] = [];
            const currentFields = STEP_FIELDS[0].map(idx => sections[0].fields[idx]);
            for (const field of currentFields) {
                if (field.required && !state.values[field.name]) {
                    missing.push(field.name);
                }
            }
            if (missing.length > 0) {
                setRequiredErrors(missing);
                return;
            }
            setRequiredErrors([]);
        }
        setStep(prev => prev + 1);
    }

    function handleBack(e: React.MouseEvent) {
        e.preventDefault();
        setRequiredErrors([]);
        setStep(prev => prev - 1);
    }

    if (sections.length === 0) {
        return <RowSpinner show className='mt-3'/>;
    }

    const fields = sections[0].fields;

    const renderStepIndicator = () => (
        <div className={'step-indicator d-flex justify-content-center mb-3'}>
            {[0, 1, 2].map(i => (
                <div key={i} className={`step-dot ${i === step ? 'active' : ''} ${i < step ? 'completed' : ''} ${i > step ? 'upcoming' : ''}`} />
            ))}
        </div>
    );

    const renderStep0 = () => (
        <Row className={'d-flex justify-content-center'}>
            {STEP_FIELDS[0].map(idx => (
                <Col key={idx} xs={10} className={requiredErrors.includes(fields[idx].name) ? 'field-required-error' : ''}>
                    <FormField
                        {...fields[idx]}
                        autoComplete={'off'}
                        serverErrors={state.errors[fields[idx].name]}
                        value={state.values[fields[idx].name] || getEmptyValue(fields[idx].type)}
                        onChange={onChange}
                        useCMS={false}
                    />
                </Col>
            ))}
        </Row>
    );

    const renderStep1 = () => (
        <Row className={'d-flex justify-content-center'}>
            {STEP_FIELDS[1].map(idx => (
                <Col key={idx} xs={10}>
                    <FormField
                        {...fields[idx]}
                        autoComplete={'off'}
                        serverErrors={state.errors[fields[idx].name]}
                        value={state.values[fields[idx].name] || getEmptyValue(fields[idx].type)}
                        onChange={onChange}
                        useCMS={false}
                    />
                </Col>
            ))}
        </Row>
    );

    const renderStep2 = () => (
        <Row className={'d-flex justify-content-center'}>
            <Col xs={10} className={'mb-2'}>
                <Accordion>
                    <Card>
                        <Accordion.Toggle as={Card.Header} className='cursor' eventKey="0">
                            <p className='pb-0 mb-0'>{getContent('add_tag__select_available_tag__title')}</p>
                        </Accordion.Toggle>
                        <Accordion.Collapse eventKey="0">
                            <Card.Body>
                                {availableTags.map((tag, i) =>
                                    assignedTags.includes(tag)
                                        ? null :
                                        <Badge
                                            key={i.toString()}
                                            className={'mr-1 ml-0 cursor badge-tags'}
                                            onClick={() => addTag(tag)}
                                        >{tag}</Badge>
                                )}
                            </Card.Body>
                        </Accordion.Collapse>
                    </Card>
                </Accordion>
                <InputGroup className={'mt-2'}>
                    <Col sm={6} className='m-0 p-0 d-flex justify-content-center'>
                        <Form.Control
                            type={'text'}
                            autoComplete={'off'}
                            placeholder={getContent('add_tag__create__form__text_placeholder')}
                            value={createTagValue}
                            onChange={(e) => setCreateTagValue(e.target.value)}
                        />
                    </Col>
                    <Col className='m-0 d-flex justify-content-center'>
                        <Button type={'button'} onClick={createTag} className={'create-tag-button pr-0'} size={'sm'}>{getContent('add_tag__create__form__button')}</Button>
                    </Col>
                </InputGroup>
            </Col>
            <Col xs={10} className={'d-flex flex-wrap'}>
                {assignedTags.length > 0 ? assignedTags.map((t, i) => (
                    <div key={i.toString()} className={'d-flex align-items-center mr-3 mt-2'}>
                        <Badge className={'badge-tags'}>{t}</Badge>
                        <Button variant={'link'} onClick={() => removeTag(t)} className={'p-0 ml-1'}>x</Button>
                    </div>
                )) : <div className={'bg-light p-3 text-center w-100'}>{getContent('admin__form__tags_assigned__empty') || 'No hay tags asignados'}</div>}
            </Col>
        </Row>
    );

    const nextLabel = getContent('home__form__next_button') !== 'home__form__next_button'
        ? getContent('home__form__next_button') : 'Siguiente';
    const backLabel = getContent('home__form__back_button') !== 'home__form__back_button'
        ? getContent('home__form__back_button') : 'Atrás';

    return (
        <Container className={'sticky-top h-100 mb-0'}>
            <Form autoComplete={'off'} onSubmit={submitHandler(onSubmit)} className={'text-center mb-3 sticky-top first'}>
                <h3 className={'text-primary py-3 my-3'}>{getContent('home__form__form_title')}</h3>

                {renderStepIndicator()}

                <Form.Group className={'text-left'}>
                    <Container>
                        <div className={'step-carousel-wrapper'}>
                            <div className={'step-carousel'} style={{transform: `translateX(-${step * 33.333}%)`}}>
                                <div className={'step-panel'}>{renderStep0()}</div>
                                <div className={'step-panel'}>{renderStep1()}</div>
                                <div className={'step-panel'}>{renderStep2()}</div>
                            </div>
                        </div>

                        <Row className={'d-flex justify-content-center mt-3 step-buttons'}>
                            <Col xs={10} className={'d-flex justify-content-center'}>
                                {step > 0 && (
                                    <Button
                                        className={'m-2 flex-grow-1'}
                                        variant={'outline-primary'}
                                        onClick={handleBack}
                                    >
                                        {backLabel}
                                    </Button>
                                )}
                                {step < 2 && (
                                    <Button
                                        className={'m-2 flex-grow-1 registerParticipant'}
                                        variant={'primary'}
                                        onClick={handleNext}
                                    >
                                        {nextLabel}
                                    </Button>
                                )}
                                {step === 2 && (
                                    <Button
                                        className={'m-2 flex-grow-1 registerParticipant'}
                                        variant={'primary'}
                                        type={'submit'}
                                        disabled={state.sending}
                                    >
                                        {!state.sending && getContent('home__form__register_button')}
                                        {state.sending && <Spinner animation={'border'} size={'sm'} color={'#FFFFFF'} />}
                                    </Button>
                                )}
                            </Col>
                        </Row>
                    </Container>
                </Form.Group>
            </Form>
        </Container>
    );
};

export default CreatePatient;
